import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { trackFunnelEvent, trackJourneyEvent } from "Config/analytics";
import { useUserInfo } from "GlobalStates/User";
import { refreshUserSession } from "./apis/registerUser";

const toUser = (session) => ({
  token: session.accessToken,
  tier: session.tier,
  name: session.name,
  completedCollegeFinder: session.completedCollegeFinder,
  picture: session.picture,
  id: session.id,
  email: session.email,
});

const fetchSessionResult = async () => {
  try {
    const response = await refreshUserSession();
    return { session: response.data };
  } catch (error) {
    return {
      failure: true,
      errorCategory: error?.code || "refresh_failure",
    };
  }
};

export const AuthSessionBootstrap = ({ children }) => {
  const { user, setUser } = useUserInfo();
  const [bootstrapComplete, setBootstrapComplete] = useState(
    Boolean(user?.token)
  );
  const { data } = useQuery({
    queryKey: ["auth", "session-bootstrap"],
    queryFn: fetchSessionResult,
    enabled: !bootstrapComplete && !user?.token,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (user?.token) {
      setBootstrapComplete(true);
      return;
    }

    if (data?.session?.accessToken) {
      trackFunnelEvent("login", {
        method: "refresh_token",
        auth_surface: "app_bootstrap",
      });
      trackJourneyEvent("authentication_succeeded", {
        terminal_status: "succeeded",
        source: "refresh_token",
      });
      setUser(toUser(data.session));
      setBootstrapComplete(true);
      return;
    }

    if (data?.failure) {
      trackJourneyEvent("authentication_failed", {
        error_category: data.errorCategory,
        terminal_status: "failed",
        source: "refresh_token",
      });
      setBootstrapComplete(true);
    }
  }, [data, setUser, user?.token]);

  if (!bootstrapComplete) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          alignItems: "center",
          display: "flex",
          height: "100vh",
          justifyContent: "center",
        }}
      >
        Restoring your session…
      </div>
    );
  }

  return children;
};
